import { backendUrl } from './App';
import { SessionDataType } from './contexts/SessionData';
import { ContactMethods } from './contexts/UserData';
import { OrderSubmissionError, getSafeBackendErrorDetail } from './telegramSupport';

export type SharedOrderData = Omit<SessionDataType, 'changeNeeded' | 'changeAmount'> & {
  paymentMethod?: 'cash' | 'bank_card';
  changeNeeded?: 'Yes' | 'No';
  changeAmount?: number | null;
  contacts?: ContactMethods;
  promo_code?: string | null;
  cart_total_cost?: number;
};

export type OrderSubmissionRequest = {
  cart_items: unknown[];
  order_data: SharedOrderData;
};

export type AdminDeliveryOrderSubmissionRequest = {
  apiKey: string;
  cart_items: [];
  order_data: SharedOrderData & {
    cart_total_cost: number;
    courier_pickup_interval: number;
    deliveryNeeded: 'Yes';
  };
};

export type OrderSubmissionResponse = {
  receipt?: string | null;
  message?: unknown;
  detail?: unknown;
  error?: unknown;
};

const isUnknownRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

/**
 * submitOrderRequest
 *
 * Provides the typed transport for customer-menu orders. It accepts the
 * customer's product array, depends only on the app-selected backend URL, and
 * throws a normalized submission error when the backend rejects the request.
 */
export async function submitOrderRequest(request: OrderSubmissionRequest): Promise<OrderSubmissionResponse> {
  const response = await fetch(`${backendUrl}/tg_miniapp_replay`, {
    method: 'POST',
    headers: {
      'ngrok-skip-browser-warning': 'true',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw new OrderSubmissionError(response.status, getSafeBackendErrorDetail(payload));
  }

  return isUnknownRecord(payload) ? payload as OrderSubmissionResponse : {};
}

/**
 * submitAdminDeliveryOrderRequest
 *
 * Sends the staff-entered delivery-only payload to the integration endpoint
 * without changing the customer checkout transport. It accepts the required
 * API key and an intentionally empty cart, depends only on the app-selected
 * backend URL, converts the backend's JSON string into the existing receipt
 * shape, and normalizes both object and string error responses.
 */
export async function submitAdminDeliveryOrderRequest(
  request: AdminDeliveryOrderSubmissionRequest,
): Promise<OrderSubmissionResponse> {
  const response = await fetch(`${backendUrl}/new_order_for_delivery`, {
    method: 'POST',
    headers: {
      'ngrok-skip-browser-warning': 'true',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const errorPayload = typeof payload === 'string' ? { detail: payload } : payload;
    throw new OrderSubmissionError(response.status, getSafeBackendErrorDetail(errorPayload));
  }

  if (typeof payload === 'string') {
    return { receipt: payload };
  }
  return isUnknownRecord(payload) ? payload as OrderSubmissionResponse : {};
}
